import authService from './authService';
import {
  PaymentDefinition,
  PaymentDefinitionFormData,
  PaymentDefinitionSearchModel,
  PaymentMethod,
} from '../types/paymentDefinition';
import { PaginatedResponse, ApiResponse } from '../types/common';

class PaymentDefinitionService {
  private async getApiClient() {
    return authService.getApiClient();
  }

  // ===== PaymentDefinition CRUD =====

  async getAll(searchModel: PaymentDefinitionSearchModel): Promise<PaginatedResponse<PaymentDefinition>> {
    const params = new URLSearchParams({
      page: searchModel.page.toString(),
      page_size: searchModel.pageSize.toString(),
    });
    if (searchModel.sales_channel_id) params.set('sales_channel_id', searchModel.sales_channel_id.toString());
    if (searchModel.payment_method_id) params.set('payment_method_id', searchModel.payment_method_id.toString());
    if (searchModel.company_id) params.set('company_id', searchModel.company_id.toString());

    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/paymenthub/payment-definitions?${params}`);

    if (!response.ok) throw new Error('Falha ao buscar definições de pagamento');

    const json = await response.json();
    const items = Array.isArray(json.data) ? json.data : [];
    const pg = json.pagination ?? {};

    return {
      success: json.success ?? true,
      data: items,
      pagination: {
        page: pg.page ?? searchModel.page,
        limit: pg.page_size ?? searchModel.pageSize,
        total: pg.total ?? items.length,
        totalPages: pg.total_pages ?? 1,
      },
    };
  }

  async getById(id: number): Promise<PaymentDefinition> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/paymenthub/payment-definitions/${id}`);
    if (!response.ok) throw new Error('Falha ao buscar definição de pagamento');
    const data: ApiResponse<PaymentDefinition> = await response.json();
    return data.data!;
  }

  async create(formData: PaymentDefinitionFormData): Promise<PaymentDefinition> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post('/paymenthub/payment-definitions', formData);
    if (!response.ok) {
      const err = await response.json().catch(() => null);
      throw new Error(err?.message || 'Falha ao criar definição de pagamento');
    }
    const data: ApiResponse<PaymentDefinition> = await response.json();
    return data.data!;
  }

  async update(id: number, formData: PaymentDefinitionFormData): Promise<PaymentDefinition> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.put(`/paymenthub/payment-definitions/${id}`, formData);
    if (!response.ok) {
      const err = await response.json().catch(() => null);
      throw new Error(err?.message || 'Falha ao atualizar definição de pagamento');
    }
    const data: ApiResponse<PaymentDefinition> = await response.json();
    return data.data!;
  }

  async delete(id: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.delete(`/paymenthub/payment-definitions/${id}`);
    if (!response.ok) {
      const err = await response.json().catch(() => null);
      throw new Error(err?.message || 'Falha ao excluir definição de pagamento');
    }
  }

  // ===== Lookup: Payment Methods =====

  async listAllPaymentMethods(): Promise<PaymentMethod[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get('/financial/payment-methods?page=1&page_size=200');
    if (!response.ok) throw new Error('Falha ao buscar métodos de pagamento');
    const json = await response.json();
    return Array.isArray(json.data) ? json.data : [];
  }
}

export const paymentDefinitionService = new PaymentDefinitionService();
export default paymentDefinitionService;
